// The order lifecycle, mirroring the backend `OrderStatus` enum (ADR 0006).
//
// Each status carries a *pair* of colours, not one. The same value is asked to
// be badge text on a 16% tint of itself, a solid fill with white text on it
// (the stepper circle, the timeline tag), and a 6px dot — and the first two want
// opposite lightness in the two themes. One hex could not do it: 21 of the 33
// contrast checks failed, APPROVED scored 1.66:1 as dark-mode badge text and
// PRODUCTION 1.40:1 under white. `light` doubles as the solid fill, since it
// clears white text at 5.93:1 or better. Read them through statusMeta(), never
// directly — it resolves the pair for the current theme.
//
// The hues were chosen for separation, not taste: DRAFT and CANCELLED used to
// sit at ΔE 4.8 (indistinguishable, opposite meanings) and READY/OUT FOR
// DELIVERY at 17.7. The floor is now 14.2. statusPalette.test.js holds both
// properties. Change a value there and the test tells you what it cost.
//
// Array order is flow order and is what every picker renders; the `value` is the
// database id and is deliberately *not* in sequence, because renumbering would
// have rewritten `orders.status` and 56k history rows for nothing.
//
// `retired: true` means "still labelled, never offered": QA left the lifecycle
// but its history entries must keep resolving to a name rather than "Unknown".
export const OrderStatusEnum = Object.freeze([
  {
    // DRAFT is the initial state — no "set to DRAFT" permission; only the
    // umbrella CHANGE_STATUS can move an order back to it.
    label: 'DRAFT',
    value: 1,
    light: '#475569',
    dark: '#94a3b8'
  },
  {
    label: 'APPROVED',
    value: 2,
    light: '#6d28d9',
    dark: '#a78bfa',
    permission:'ORDER_APPROVE'
  },
  {
    // The print floor, between approval and the line. Rose: the palette test
    // requires ΔE >= 14 from every other status in both themes, and this pair
    // clears it by 38.6 (light) / 34.2 (dark) — well away from RETURNED's red.
    label: 'PRINT',
    value: 12,
    light: '#9d174d',
    dark: '#f472b6',
    permission: 'ORDER_PRINT'
  },
  {
    label: 'CUTTING',
    value: 3,
    light: '#92400e',
    dark: '#fbbf24',
    permission:'ORDER_CUTTING'
  },
  {
    label: 'SEWING',
    value: 13,
    light: '#134e4a',
    dark: '#5eead4',
    permission: 'ORDER_SEWING'
  },
  {
    label: 'READY',
    value: 5,
    light: '#155e75',
    dark: '#22d3ee',
    permission:'ORDER_READY'
  },
  {
    // The courier booking, made once the goods are ready.
    label: 'BOOKING',
    value: 9,
    light: '#1e40af',
    dark: '#60a5fa',
    permission:'ORDER_BOOKING'
  },
  {
    label: 'OUT FOR DELIVERY',
    value: 10,
    light: '#0369a1',
    dark: '#38bdf8',
    permission:'ORDER_OUT_FOR_DELIVERY'
  },
  {
    // Parked for a repair or a reschedule; releasing returns it to the status it
    // was held from, which the backend remembers in `held_from_status`.
    label: 'ON HOLD',
    value: 11,
    light: '#86198f',
    dark: '#e879f9',
    permission:'ORDER_HOLD'
  },
  {
    label: 'DELIVERED',
    value: 6,
    light: '#065f46',
    dark: '#34d399',
    permission:'ORDER_DELIVERED'
  },
  {
    label: 'RETURNED',
    value: 7,
    light: '#b91c1c',
    dark: '#f87171',
    permission:'ORDER_RETURNED'
  },
  {
    label: 'CANCELLED',
    value: 8,
    light: '#57534e',
    dark: '#a8a29e',
    permission:'ORDER_CANCELLED'
  },
  {
    label: 'QA',
    value: 4,
    light: '#52525b',
    dark: '#a1a1aa',
    permission: 'ORDER_IN_QA',
    retired: true
  }
])

// The exits: a status nobody should be able to enter without saying why, and
// the ones that put finished goods back into Ready Stock. Mirrors
// OrderStatus::requiresReason() on the backend, which enforces it.
export const EXIT_STATUSES = Object.freeze([8, 7, 11]);

// Statuses from which finished goods exist, so cancelling or returning has
// something to restock — mirrors OrderStatus::finishedGoods() on the server.
//
// HOLD (11) is deliberately absent, and used not to be. An order can be held
// from any open status, so being on hold says nothing about whether it passed
// production: listing it here asked the operator to mark every line Sellable or
// Damaged when cancelling an order held at the printers, where nothing finished
// exists to restock. Resolve it through the order's heldFromStatus instead —
// see restocksFrom(), which mirrors Order::hasFinishedGoods().
export const PRODUCED_STATUSES = Object.freeze([5, 9, 10, 6]);

// Open/active: committed but not finished. Mirrors OrderStatus::open().
export const OPEN_STATUSES = Object.freeze([2, 12, 3, 13, 5, 9, 10, 11]);

/**
 * How long an order may sit on hold before it reads as stuck. Mirrors
 * OrderStatus::HELD_TOO_LONG_DAYS — the server decides which orders count, this
 * is only for saying the number out loud.
 */
export const HELD_TOO_LONG_DAYS = 7;

// Worth filtering a list by. Retired statuses hold no orders once their
// migration has run, so offering one only ever returns an empty page — but
// `statusMeta` must still resolve them, which is why they stay in the array.
export const filterableStatuses = OrderStatusEnum.filter((s) => !s.retired);

// Ids used by the helpers below; the enum's values are not in flow order.
const HOLD = 11;

export const requiresReason = (status) => EXIT_STATUSES.includes(status);

/**
 * Whether an order in this status has finished goods to put back into Ready
 * Stock on a cancel or a return. Mirrors Order::hasFinishedGoods().
 *
 * On HOLD the current status is uninformative — resolve through where it was
 * held from, which is the stage it will be released back to. A hold placed
 * during PRINT or PRODUCTION restocks nothing; one placed from READY onwards
 * does. `heldFromStatus` is null on holds written before the column existed, in
 * which case nothing is claimed and no per-line decision is demanded — the
 * server makes the same call, and is the one that actually moves the stock.
 */
export const restocksFrom = (currentStatus, heldFromStatus = null) => {
  const effective =
    currentStatus === HOLD && heldFromStatus != null ? heldFromStatus : currentStatus;

  return PRODUCED_STATUSES.includes(effective);
};

// Transition-scoped status permissions (ADR 0004), mirroring the backend's
// StatusChangeAuthorizer: a user may move an order into a status if they hold
// the umbrella CHANGE_STATUS, or that status's own permission.
export const canSetStatus = (permissions = [], status) =>
  permissions.includes('CHANGE_STATUS') ||
  (!!status?.permission && permissions.includes(status.permission));

// The subset of statuses the user is allowed to set — use this to build any
// status picker so web and mobile share one barrier. Retired statuses are never
// offered, whatever permissions the user holds.
export const settableStatuses = (permissions = []) =>
  OrderStatusEnum.filter((s) => !s.retired && canSetStatus(permissions, s));

// Whether the user can set at least one status (gates the status control itself).
export const canChangeAnyStatus = (permissions = []) =>
  settableStatuses(permissions).length > 0;
